import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisAdCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisAdCard1/LomavisAdCard1';

interface SortOption {
  name: string;
  title: string;
}

interface LomavisBestPerformingAdsProps {
  title: string;
  itemsToShow?: number;
  data: Array<Record<string, any>>;
  sortOptions: SortOption[];
  customFilterOptions?: Array<{ value: string; label: string }>;
  selectCustomFilter?: (item: string) => void;
  currentCustomFilter?: string;
}

function getValueByPath(obj: any, path: string): any {
  return path
    .split(/[.[\]]+/)
    .filter(Boolean)
    .reduce((o, key) => (o && typeof o === 'object' ? o[key] : undefined), obj);
}

export const LomavisBestPerformingAds: React.FC<
  LomavisBestPerformingAdsProps
> = ({
  title,
  data,
  sortOptions,
  customFilterOptions,
  currentCustomFilter,
  selectCustomFilter
}) => {
  const t = translateComponent('ILomavisBestPerformingAds');
  const { t: translate } = useTranslate();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const customDropdownRef = useRef<HTMLDivElement | null>(null);

  const [sortOption, setSortOption] = useState(sortOptions[0]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [sortedData, setSortedData] = useState(data);
  const [itemsToShow, setItemsToShow] = useState(3);
  const [showCustomDropdown, setShowCustomDropdown] = useState(false);

  const levelSubtitle = React.useMemo(() => {
    switch (currentCustomFilter) {
      case 'ads':
        return translate('adsAnalytics.level.ads');
      case 'adsets':
        return translate('adsAnalytics.level.adsets');
      case 'campaigns':
        return translate('adsAnalytics.level.campaigns');
      default:
        return undefined;
    }
  }, [currentCustomFilter, translate]);

  useEffect(() => {
    const valuePath = `analytics.${sortOption?.name}`;
    const getSortValue = (row: any) => {
      const raw = getValueByPath(row, valuePath);
      if (raw && typeof raw === 'object' && 'value' in raw) {
        return raw?.value;
      }
      return raw;
    };
    const newData = [...data].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      const na =
        typeof va === 'number' ? va : va == null ? null : Number(va) || 0;
      const nb =
        typeof vb === 'number' ? vb : vb == null ? null : Number(vb) || 0;

      if (na === null && nb === null) return 0;
      if (na === null) return 1;
      if (nb === null) return -1;
      return nb - na;
    });
    setSortedData(newData);
  }, [sortOption, data]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showCustomDropdown &&
        customDropdownRef.current &&
        !customDropdownRef.current.contains(e.target as Node)
      ) {
        setShowCustomDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCustomDropdown]);

  return (
    <LomavisCard1 showDownloadButton={false} title={title} showMoreInfo={false}>
      <div className="d-flex flex-column gap-1 w-100">
        <div className="w-100 d-flex justify-content-between">
          <ButtonGroup aria-label="Items to show">
            <Button
              variant="secondary"
              size="sm"
              className={
                itemsToShow === 3
                  ? 'bg-secondary border-secondary'
                  : 'bg-light text-dark border-secondary'
              }
              onClick={() => setItemsToShow(3)}
            >
              {t('top3')}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className={
                itemsToShow === 6
                  ? 'bg-secondary border-secondary'
                  : 'bg-light text-dark border-secondary'
              }
              onClick={() => setItemsToShow(6)}
            >
              {t('top6')}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className={
                itemsToShow === sortedData.length
                  ? 'bg-secondary border-secondary'
                  : 'bg-light text-dark border-secondary'
              }
              onClick={() => setItemsToShow(sortedData.length)}
            >
              {t('all')}
            </Button>
          </ButtonGroup>

          <div className="d-flex gap-2">
            {currentCustomFilter && customFilterOptions?.length > 0 && (
              <div className="dropdown text-end" ref={customDropdownRef}>
                <button
                  className="btn btn-secondary btn-sm dropdown-toggle"
                  type="button"
                  aria-expanded={showCustomDropdown}
                  onClick={() => setShowCustomDropdown((prev) => !prev)}
                >
                  {
                    customFilterOptions?.find(
                      (item) => item?.value === currentCustomFilter
                    )?.label
                  }
                </button>
                <ul
                  className={`dropdown-menu${
                    showCustomDropdown ? ' show' : ''
                  }`}
                >
                  {customFilterOptions.map((item) => (
                    <li key={item.value} className="w-100 p-1">
                      <button
                        className={`dropdown-item${
                          item?.value === currentCustomFilter ? ' active' : ''
                        }`}
                        type="button"
                        onClick={() => {
                          selectCustomFilter?.(item.value);
                          setShowCustomDropdown(false);
                        }}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="dropdown text-end" ref={dropdownRef}>
              <button
                className="btn btn-secondary btn-sm dropdown-toggle"
                type="button"
                aria-expanded={showDropdown}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                {sortOption?.title}
              </button>
              <ul className={`dropdown-menu${showDropdown ? ' show' : ''}`}>
                {sortOptions.map((item) => (
                  <li key={item?.name} className="w-100 p-1">
                    <button
                      className={`dropdown-item${
                        item?.name === sortOption?.name ? ' active' : ''
                      }`}
                      type="button"
                      onClick={() => {
                        setSortOption(item);
                        setShowDropdown(false);
                      }}
                    >
                      {item?.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="row">
          {sortedData && sortedData.length > 0 ? (
            sortedData.slice(0, itemsToShow).map((item, index) => (
              <div
                key={item.id ?? index}
                className="col-12 col-sm-6 col-md-4 g-2"
              >
                <LomavisAdCard1
                  image={item?.preview_image_url}
                  title={item?.text}
                  subtitle={
                    levelSubtitle ?? item?.profile?.username ?? item?.id
                  }
                  analytics={item?.analytics}
                />
              </div>
            ))
          ) : (
            <div className="col-12 h-400px d-flex justify-content-center align-items-center">
              <span className="text-center text-muted py-4 fs-3">
                {t?.('noDataAvailable')}
              </span>
            </div>
          )}
        </div>
      </div>
    </LomavisCard1>
  );
};
